// First: see the module for why this is not a call in this file (#1285).
import "@/features/poll/utils/installConsoleErrorBuffer";
import {StrictMode} from "react";
import ReactDOM from "react-dom/client";
import {RouterProvider, createRouter} from "@tanstack/react-router";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

import {routeTree} from "./routeTree.gen";
import {deferStyles} from "./utils/load";
import {RouterError} from "@/components/RouterError";
import Config from "@/config";
import {ErrorBoundary} from "./features/system/components/ErrorBoundary";
import {initGoogleTag, logEvent, getCleanUrl} from "@/utils/analytics";
import {RELEASE} from "@/constants/release";
import {ensureVersionKeyCookie} from "@/utils/versionKey";
import {createBetterStackTransport} from "@/utils/sentryTransport";
import {
  scrubBreadcrumb,
  scrubEvent,
  scrubReplayEvent,
} from "@/utils/sentryScrub";

import "@/styles/global.css";
import "@/styles/main.css";
import "@/styles/critical-font.css";

// Initialize Google tag for Google Analytics.
// Method `window.gtag()` must be available as soon as possible for tracking events and errors.
initGoogleTag();

// Pin this device to one arm of the Cloudflare split. Set as early as possible:
// requests made before the cookie exists are routed by percentage, so anything
// still unpinned can be answered by either version.
ensureVersionKeyCookie();

// reloads the app if there is an error fetching an outdated chunk due to a new build deployed
// https://github.com/TanStack/router/issues/1276#issuecomment-1989333413
window.addEventListener("vite:preloadError", () => {
  logEvent("preload_error");
  window.location.reload();
});

// Create a new query client instance
const queryClient = new QueryClient();

// Undefined on any build whose HEAD carried no tag -- see constants/release.ts
// for why that is normalized there rather than at each consumer.
console.info("Release:", RELEASE);

// Create a new router instance
export const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultErrorComponent: RouterError,
});

// subscribe to the router changes when navigation has fully resolved (captures all resolved navigations including initial mount)
router.subscribe("onResolved", () => {
  // log the page view to Google Analytics when the route changes
  // https://developers.google.com/tag-platform/gtagjs/reference/events#page_view
  // Use the resolved router state location to construct the correct absolute URL,
  // avoiding the race condition where window.location.href has not yet been synced by the browser.
  const targetUrl = window.location.origin + router.state.location.href;
  logEvent("page_view", {
    page_location: getCleanUrl(targetUrl),
  });
});

if (process.env.NODE_ENV === "production") {
  // Named once: the transport routes errors to this same project, so the two
  // must never drift apart.
  const sentryDsn = Config.SENTRY_URL;

  Sentry.init({
    dsn: sentryDsn,
    // debug: true,

    // Fans errors out to Better Stack as a second destination; undefined while
    // that DSN is empty -- see utils/sentryTransport.
    transport: createBetterStackTransport(
      Config.BETTER_STACK_SENTRY_URL,
      sentryDsn,
    ),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({beforeAddRecordingEvent: scrubReplayEvent}),
      Sentry.tanstackRouterBrowserTracingIntegration(router),

      // Filter out third-party errors that are not related to the application.
      // https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-thirdpartyerrorfilterintegration
      Sentry.thirdPartyErrorFilterIntegration({
        // Specify the application keys that you specified in the Sentry bundler plugin.
        filterKeys: ["trendaro-client"],
        // Specify the behavior for third-party frames.
        behaviour: "drop-error-if-contains-third-party-frames",
      }),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // https://docs.sentry.io/platforms/javascript/tracing/trace-propagation/
    // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // When using strings, partial matches will be filtered out.
    // If you need to filter by exact match, use regex patterns instead.
    // https://docs.sentry.io/platforms/javascript/configuration/filtering/#filtering-error-events
    ignoreErrors: [
      // ignore errors from fetching an outdated chunk due to a new build deployed
      "Failed to fetch dynamically imported module",
      "Unable to preload CSS",
      "'text/html' is not a valid JavaScript MIME type",
      "AbortError: The operation was aborted",
    ],
    // Release version from Git tag.
    release: RELEASE,

    // RTDB addresses carry the Firebase ID token (?auth=), and Sentry copies
    // addresses into titles, breadcrumbs and spans -- see utils/sentryScrub.
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
}

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root")!;

// Defer the loading of non-critical styles, to avoid render-blocking
deferStyles("deferred-styles");

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </QueryClientProvider>
    </StrictMode>,
  );
}
